import { useState } from 'react';
import { Button, Group, NumberInput, Select, Stack } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { BillingDetail } from './hooks/useBilling';
import { useUpdateBillingMutation } from './hooks/useUpdateBillingMutation';

const billingTypeOptions = [
  { value: 'FIXED', label: 'Fixed' },
  { value: 'PER_UNIT', label: 'Per unit' },
];

function toNumberOrEmpty(val: number | string | null | undefined): number | string {
  if (val === null || val === undefined) return '';
  const n = Number(val);
  return Number.isNaN(n) ? '' : n;
}

function toNullableNumber(val: number | string): number | null {
  if (val === '' || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}

function toFirstOfMonth(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

export function BillingEditForm({
  billingDetail,
  onClose,
}: {
  billingDetail: BillingDetail;
  onClose: () => void;
}) {
  const { updateBilling, isLoading } = useUpdateBillingMutation();
  const [billingType, setBillingType] = useState<string | null>(billingDetail.billingType);
  const [billingAmount, setBillingAmount] = useState<number | string>(
    toNumberOrEmpty(billingDetail.billingAmount),
  );
  const [minimumBillingAmount, setMinimumBillingAmount] = useState<number | string>(
    toNumberOrEmpty(billingDetail.minimumBillingAmount),
  );
  const [billingStartDate, setBillingStartDate] = useState<string | null>(
    billingDetail.billingStartDate,
  );
  const [statedClientCount, setStatedClientCount] = useState<number | string>(
    toNumberOrEmpty(billingDetail.statedClientCount),
  );

  const handleSubmit = async () => {
    try {
      await updateBilling({
        companyId: billingDetail.companyId,
        billingType: billingType as 'FIXED' | 'PER_UNIT' | null,
        billingAmount: toNullableNumber(billingAmount),
        minimumBillingAmount: toNullableNumber(minimumBillingAmount),
        billingStartDate: toFirstOfMonth(billingStartDate),
        statedClientCount: toNullableNumber(statedClientCount),
      });
      onClose();
    } catch (error) {
      notifyAndLogError('Failed to update billing', 'Please try again.', error);
    }
  };

  return (
    <Stack gap="md">
      <Select
        label="Billing type"
        data={billingTypeOptions}
        value={billingType}
        onChange={setBillingType}
        clearable
      />
      <NumberInput
        label="Billing amount (£)"
        value={billingAmount}
        onChange={setBillingAmount}
        min={0}
        decimalScale={2}
        fixedDecimalScale
      />
      <NumberInput
        label="Minimum billing amount (£)"
        value={minimumBillingAmount}
        onChange={setMinimumBillingAmount}
        min={0}
        decimalScale={2}
        fixedDecimalScale
      />
      <NumberInput
        label="Total clients (stated by the firm)"
        description="What the firm told us they have, to compare against the billable units on the platform"
        value={statedClientCount}
        onChange={setStatedClientCount}
        min={0}
        allowDecimal={false}
      />
      <MonthPickerInput
        label="Billing start date"
        placeholder="Select month"
        value={billingStartDate}
        onChange={setBillingStartDate}
        clearable
      />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
